import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { CreditCard, CheckCircle, Shield, Clock, DollarSign, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const SetupPayment = () => {
  const navigate = useNavigate();
  const { token, user, company } = useAuth();
  const { toast } = useToast();
  const [currency, setCurrency] = useState('usd');
  const [isLoading, setIsLoading] = useState(false);

  const pricing = {
    usd: { symbol: '$', setup: '9.99', monthly: '5.99', label: 'USD' },
    cad: { symbol: 'C$', setup: '9.99', monthly: '5.99', label: 'CAD' },
    eur: { symbol: '€', setup: '9.99', monthly: '5.99', label: 'EUR' },
    gbp: { symbol: '£', setup: '9.99', monthly: '5.99', label: 'GBP' }
  };

  const handleStartTrial = async () => {
    setIsLoading(true);

    try {
      // Get origin URL for success/cancel redirects
      const originUrl = window.location.origin;

      // Create checkout session
      const response = await axios.post(
        `${API}/stripe/create-checkout-session`,
        null,
        {
          params: {
            payment_type: 'setup_fee',
            origin_url: originUrl,
            currency: currency
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Redirect to Stripe checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create payment session',
        variant: 'destructive'
      });
      setIsLoading(false);
    }
  };

  const handleSkipForNow = () => {
    // Allow access to app but remind them to pay
    navigate('/home');
    toast({
      title: 'Payment Required',
      description: 'Please complete payment setup to continue using Hines Time after your trial period.',
      variant: 'default',
      duration: 10000
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center p-4">
      <Card className="max-w-3xl w-full shadow-2xl">
        <CardHeader className="bg-gradient-to-r from-blue-600 to-green-600 text-white rounded-t-lg">
          <CardTitle className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
                <CreditCard className="w-10 h-10 text-blue-600" />
              </div>
            </div>
            <h2 className="text-3xl font-bold">Complete Your Setup</h2>
            <p className="text-blue-100 mt-2 font-normal">Start your 30-day free trial</p>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-8">
          {/* Welcome Message */}
          <div className="mb-8 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Welcome to Hines Time, {company?.company_name}!
            </h3>
            <p className="text-gray-600">
              You're almost ready to start tracking time and managing your workforce.
            </p>
          </div>

          {/* Trial Information */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 mb-8">
            <div className="flex items-start space-x-4">
              <Clock className="w-12 h-12 text-blue-600 flex-shrink-0" />
              <div>
                <h4 className="font-bold text-blue-900 mb-2">30-Day Free Trial Starts Now</h4>
                <ul className="space-y-2 text-sm text-blue-800">
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>No charges for 30 days - try all features risk-free</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>Credit card required to prevent abuse</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>Cancel anytime during trial - zero charges</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>After trial: ${pricing[currency].setup} setup + ${pricing[currency].monthly}/user/month</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Currency Selection */}
          <div className="mb-8">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Select Your Currency
            </label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger className="h-12">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="usd">🇺🇸 USD - United States Dollar</SelectItem>
                <SelectItem value="cad">🇨🇦 CAD - Canadian Dollar</SelectItem>
                <SelectItem value="eur">🇪🇺 EUR - Euro</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Pricing Breakdown */}
          <Card className="bg-gradient-to-br from-green-50 to-blue-50 border-2 border-green-200 mb-8">
            <CardContent className="p-6">
              <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                <DollarSign className="w-5 h-5 mr-2 text-green-600" />
                After Your Free Trial
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-700">One-time Setup Fee</span>
                  <span className="font-bold text-gray-900">
                    {pricing[currency].symbol}{pricing[currency].setup}
                  </span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Per User (Monthly)</span>
                  <span className="font-bold text-gray-900">
                    {pricing[currency].symbol}{pricing[currency].monthly}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-2">
                  <span className="text-gray-700">Starting with <strong>1 user</strong></span>
                  <span className="text-lg font-bold text-green-600">
                    {pricing[currency].symbol}{(parseFloat(pricing[currency].setup) + parseFloat(pricing[currency].monthly)).toFixed(2)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Security Badge */}
          <div className="flex items-center justify-center space-x-2 text-sm text-gray-600 mb-8">
            <Shield className="w-5 h-5 text-green-600" />
            <span>Secure payment powered by Stripe</span>
          </div>

          {/* Action Buttons */}
          <div className="space-y-4">
            <Button
              onClick={handleStartTrial}
              disabled={isLoading}
              className="w-full h-14 bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-lg font-semibold"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Creating checkout session...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5 mr-2" />
                  Start 30-Day Free Trial
                </>
              )}
            </Button>
            
            <Button
              onClick={handleSkipForNow}
              variant="outline"
              className="w-full h-12"
            >
              Skip for Now (Limited Access)
            </Button>
          </div>

          <p className="text-xs text-center text-gray-500 mt-6">
            By continuing, you agree to our Terms of Service and Privacy Policy.
            You will not be charged during your 30-day free trial period.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default SetupPayment;
